"use client";

import { useSearchParams } from "next/navigation";
import { useState } from "react";

const FILTER_KEYS = [
	"category",
	"seniority",
	"salary",
	"remoteType",
	"eligibility",
	"jobType",
	"q",
	"loc",
] as const;

/**
 * "Save this search" — persists the current /jobs filter set so the daily
 * new-jobs-alerts cron can email the user new matches. Reads filters from the
 * URL (no server threading); posts to /api/saved-searches.
 */
export function SaveSearchButton({
	locale,
	labels,
	onSaved,
}: {
	locale: string;
	labels: { save: string; saved: string };
	/** Optional hook fired after a successful save — lets a wrapping surface
	 * (e.g. RepeatSearchNudge) fire its own analytics without duplicating the
	 * save POST. Not called on error or on the 401→login redirect. */
	onSaved?: () => void;
}) {
	const sp = useSearchParams();
	const [state, setState] = useState<"idle" | "saving" | "saved" | "error">(
		"idle",
	);

	async function save() {
		setState("saving");
		const filters: Record<string, string> = {};
		for (const k of FILTER_KEYS) {
			const v = sp.get(k);
			if (v) filters[k] = v;
		}
		try {
			const res = await fetch("/api/saved-searches", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ filters, locale }),
			});
			if (res.status === 401) {
				window.location.href = `/${locale}/login`;
				return;
			}
			setState(res.ok ? "saved" : "error");
			if (res.ok) onSaved?.();
		} catch {
			setState("error");
		}
	}

	return (
		<button
			type="button"
			onClick={save}
			disabled={state === "saving" || state === "saved"}
			className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
		>
			{state === "saved" ? `✓ ${labels.saved}` : labels.save}
		</button>
	);
}
